<template>
  <v-card
    data-cy="pi-card"
  >
    <v-card-title class="title mb-0 pb-0">
      PIs ({{ countMembers }})
    </v-card-title>
    <v-card-text
      :class="{ presentation: getPresentMode }">
      <div 
        v-for="(item, i) in dataset.dataset_pi" 
        :key="i"
      >       
        <font-awesome-icon
            v-if="editMode"
            size="lg"
            class="mx-1"
            :icon="['far', 'edit']"
            @click="editPI(item)"
          >
          </font-awesome-icon>
        <font-awesome-icon
            v-if="editMode"
            size="lg"
            class="mx-1"
            :icon="['far', 'trash']"
            @click="deletePI(item)"
            :data-cy="`pi-delete-${dataset.dataset_pi.length-i}`"
          >
          </font-awesome-icon>
   
        <a v-bind:href="'mailto:' + item.email_address">{{ item.first_name}} {{ item.last_name }}</a>
        <a 
          v-bind:href="item.personal_website" 
          target="_blank"
          style="text-decoration: none"
        >
        <font-awesome-icon
            v-if="!editMode && item.personal_website"
            size="lg"
            class="mx-1"
            target="_blank"
            :icon="['far', 'home']"
          >
          </font-awesome-icon>
        </a>
        <a 
          v-bind:href="item.department_website" 
          target="_blank"
          style="text-decoration: none"
        >
          <font-awesome-icon
            v-if="!editMode && item.department_website" 
            size="lg"
            class="mx-1"
            target="_blank"
            :icon="['far', 'building']"
          >
          </font-awesome-icon>
        </a>
        <v-slide-y-transition>
          <div v-if="editedPIIndex===i">
            <v-text-field
              v-model="editedPI.first_name"
              :error-messages="nameFieldErrors($v.editedPI.first_name)"
              :hint="`${editedPI.first_name.length}/40 Characters`"
              label="PI First Name"
              @input="$v.editedPI.first_name.$touch()"
              @blur="$v.editedPI.first_name.$touch()">
            </v-text-field>
            <v-text-field
              v-model="editedPI.last_name"
              :error-messages="nameFieldErrors($v.editedPI.last_name)"
              :hint="`${editedPI.last_name.length}/40 Characters`"
              label="PI Last Name"
              @input="$v.editedPI.last_name.$touch()"
              @blur="$v.editedPI.last_name.$touch()">
            </v-text-field>
            <v-text-field
              v-model="editedPI.email_address"
              :error-messages="emailFieldErrors($v.editedPI.email_address)"
              :hint="`${editedPI.last_name.length}/40 Characters`"
              label="PI Email Address"
              @input="$v.editedPI.email_address.$touch()"
              @blur="$v.editedPI.email_address.$touch()">
            </v-text-field>
            <v-text-field
              v-model="editedPI.personal_website"
              label="PI Personal Website"
              :error-messages="urlFieldErrors($v.editedPI.personal_website)"
              :hint="`${editedPI.personal_website.length}/250 Characters`"
              @input="$v.editedPI.personal_website.$touch()"
              @blur="$v.editedPI.personal_website.$touch()">
            </v-text-field>
            <v-text-field
              v-model="editedPI.department_website"
              label="PI Department Website"
              :error-messages="urlFieldErrors($v.editedPI.department_website)"
              :hint="`${editedPI.department_website.length}/250 Characters`"
              @input="$v.editedPI.department_website.$touch()"
              @blur="$v.editedPI.department_website.$touch()">
            </v-text-field>
            <v-btn 
              flat 
              @click="savePI()"
              :disabled="$v.editedPI.$invalid"
              color="primary"
            >
              Save
            </v-btn>
            <v-btn 
              flat 
              @click="close()"
              color="primary"
            >
              Cancel
            </v-btn>
          </div>
        </v-slide-y-transition>
      </div>
      <v-slide-y-transition>
        <v-flex v-show="editMode">
          <v-text-field
            v-model="newPI.first_name"
            :error-messages="nameFieldErrors($v.newPI.first_name)"
            label="New PI First Name"
            :hint="`${newPI.first_name.length}/40 Characters`"
            @input="$v.newPI.first_name.$touch()"
            @blur="$v.newPI.first_name.$touch()"
            data-cy="new-pi-firstname"
          >
          </v-text-field>
          <v-text-field
            v-model="newPI.last_name"
            :error-messages="nameFieldErrors($v.newPI.last_name)"
            :hint="`${newPI.last_name.length}/40 Characters`"
            label="New PI Last Name"
            @input="$v.newPI.last_name.$touch()"
            @blur="$v.newPI.last_name.$touch()"
            data-cy="new-pi-lastname"  
          >
          </v-text-field>
          <v-text-field
            v-model="newPI.email_address"
            :error-messages="emailFieldErrors($v.newPI.email_address)"
            label="New PI Email"              
            :hint="`${newPI.email_address.length}/40 Characters`"
            @input="$v.newPI.email_address.$touch()"
            @blur="$v.newPI.email_address.$touch()"
            data-cy="new-pi-email"  
          >
          </v-text-field>
          <v-text-field
            v-model="newPI.personal_website"
            label="New PI Personal Website"
            :error-messages="urlFieldErrors($v.newPI.personal_website)"
            :hint="`${newPI.personal_website.length}/250 Characters`"
            @input="$v.newPI.personal_website.$touch()"
            @blur="$v.newPI.personal_website.$touch()"            
            data-cy="new-pi-personal-site"
          >
          </v-text-field>
          <v-text-field
            v-model="newPI.department_website"
            label="New PI Department Website"
            :hint="`${newPI.department_website.length}/250 Characters`"
            :error-messages="urlFieldErrors($v.newPI.department_website)"
            @input="$v.newPI.department_website.$touch()"
            @blur="$v.newPI.department_website.$touch()"                
            data-cy="new-pi-department-site"  
          >
          </v-text-field>
        </v-flex>
      </v-slide-y-transition>
    </v-card-text>
    <v-fade-transition>
      <v-card-actions v-if="editMode">
        <v-btn 
          flat 
          @click="savePI()"
          :disabled="$v.newPI.$invalid"
          color="primary"
          data-cy="add-pi-btn"
        >
          Add PI
        </v-btn>
      </v-card-actions>
    </v-fade-transition>
  </v-card>
</template>

<script>
import { required, email, url, maxLength } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import { validationMixin, name } from '../validators.js'

export default {
  mixins: [validationMixin],
  props: {
    editMode: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      defaultPI: {
        first_name: '',
        last_name: '',
        email_address: '',
        personal_website: '',
        department_website: ''
      },
      newPI: {
        first_name: '',
        last_name: '',
        email_address: '',
        personal_website: '',
        department_website: ''
      },
      editedPI: {
        first_name: '',
        last_name: '',
        email_address: '',
        personal_website: '',
        department_website: ''
      },
      editedPIIndex: -1,
      editedPIKey: -1
    }
  },
  methods: {
    ...mapActions([
      'removeItemAction',
      'editItemAction',
      'addItemAction'
    ]),
    savePI () {
      const payload = {
        array: 'dataset_pi',
        endpoint: 'pi',
        id: this.dataset.dataset_id,
        key: this.editedPIKey
      }
      if (this.editedPIIndex > -1) {
        payload.data = { ...this.editedPI }
        this.editItemAction(payload)
      } else {
        payload.data = { ...this.newPI }
        this.addItemAction(payload)
      }
      this.close()
    },
    deletePI (item) {
      const payload = {
        array: 'dataset_pi',
        endpoint: 'pi',
        key: item.key,
        id: this.dataset.dataset_id
      }
      confirm('Are you sure you want to delete this item?') &&
      this.removeItemAction(payload)
    },
    editPI (item) {
      this.editedPIIndex = this.dataset.dataset_pi.indexOf(item)
      const keys = Object.keys(this.defaultPI)
      keys.forEach(key => { this.editedPI[key] = item[key] })
      this.editedPIKey = item.key
    },
    close () {
      this.editedPI = { ...this.defaultPI }
      this.newPI = { ...this.defaultPI }
      this.editedPIKey = -1
      this.editedPIIndex = -1
    }
  },
  computed: {
    ...mapGetters([
      'getCurrentDataset',
      'getPresentMode'
    ]),
    dataset () {
      return this.getCurrentDataset
    },
    countMembers () {
      if (this.dataset.dataset_pi) {
        return this.dataset.dataset_pi.length
      } else {
        return 0
      }
    }
  },
  validations: {
    newPI: {
      first_name: {
        required,
        name,
        maxLength: maxLength(40)
      },
      last_name: {
        required,
        name,
        maxLength: maxLength(40)
      },
      email_address: {
        required,
        email,
        maxLength: maxLength(40)
      },
      personal_website: {
        url,
        maxLength: maxLength(250)
      },
      department_website: {
        url,
        maxLength: maxLength(250)
      }
    },
    editedPI: {
      first_name: {
        required,
        name,
        maxLength: maxLength(40)
      },
      last_name: {
        required,
        name,
        maxLength: maxLength(40)
      },
      email_address: {
        required,
        email,
        maxLength: maxLength(40)
      },
      personal_website: {
        url,
        maxLength: maxLength(250)
      },
      department_website: {
        url,
        maxLength: maxLength(250)
      }
    }
  }
}
</script>
